import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  LinearProgress, 
  Typography, 
  Box,
  CircularProgress,
  Fade
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles((theme) => ({
  modal: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      padding: theme.spacing(3),
      minWidth: 400,
      maxWidth: 500,
      textAlign: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    },
  },
  content: {
    padding: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  progressContainer: {
    marginBottom: theme.spacing(3),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.2)',
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
      background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
      transition: 'transform 0.3s ease',
    },
  },
  stepContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.1)',
    transition: 'all 0.3s ease',
  },
  stepIcon: {
    marginRight: theme.spacing(2),
    fontSize: 20,
  },
  stepText: {
    flex: 1,
    textAlign: 'left',
    fontSize: '0.9rem',
  },
  stepCompleted: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    border: '1px solid rgba(76, 175, 80, 0.5)',
  },
  stepError: {
    backgroundColor: 'rgba(244, 67, 54, 0.2)',
    border: '1px solid rgba(244, 67, 54, 0.5)',
  },
  stepPending: {
    opacity: 0.6,
  },
  successIcon: {
    fontSize: 60,
    color: '#4CAF50',
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 60,
    color: '#f44336',
    marginBottom: theme.spacing(2),
  },
  message: {
    marginTop: theme.spacing(2),
    fontSize: '1.1rem',
    fontWeight: 500,
  },
  spinner: {
    color: 'white',
  },
}));

const SubmissionProgressModal = ({ 
  open, 
  onClose, 
  steps = [], 
  currentStep = 0, 
  progress = 0, 
  status = 'processing', // 'processing', 'success', 'error'
  errorMessage = ''
}) => {
  const classes = useStyles();
  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setAnimatedProgress(0);
      const timer = setTimeout(() => {
        setAnimatedProgress(progress);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, progress]);

  const getStepStatus = (stepIndex) => {
    if (status === 'error' && stepIndex === currentStep) return 'error';
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep && status === 'processing') return 'processing';
    return 'pending';
  };

  const getStepIcon = (stepIndex) => {
    const stepStatus = getStepStatus(stepIndex);
    
    switch (stepStatus) {
      case 'completed':
        return <CheckCircleIcon className={classes.stepIcon} style={{ color: '#4CAF50' }} />;
      case 'error':
        return <ErrorIcon className={classes.stepIcon} style={{ color: '#f44336' }} />;
      case 'processing':
        return <CircularProgress size={20} className={classes.spinner} />;
      default:
        return <div className={classes.stepIcon} style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.3)' }} />;
    }
  };

  const getStepClass = (stepIndex) => {
    const stepStatus = getStepStatus(stepIndex);
    
    switch (stepStatus) {
      case 'completed':
        return classes.stepCompleted;
      case 'error':
        return classes.stepError;
      case 'pending':
        return classes.stepPending;
      default:
        return '';
    }
  };

  if (status === 'success') {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        className={classes.modal} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogContent className={classes.content}>
          <Fade in={true} timeout={500}>
            <Box>
              <CheckCircleIcon className={classes.successIcon} />
              <Typography variant="h5" className={classes.title}>
                Submission Successful!
              </Typography>
              <Typography className={classes.message}>
                Your exam has been submitted successfully. You will be redirected shortly.
              </Typography>
            </Box>
          </Fade>
        </DialogContent>
      </Dialog>
    );
  }

  if (status === 'error') {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        className={classes.modal} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogContent className={classes.content}>
          <Fade in={true} timeout={500}>
            <Box>
              <ErrorIcon className={classes.errorIcon} />
              <Typography variant="h5" className={classes.title}>
                Submission Failed
              </Typography>
              <Typography className={classes.message}>
                {errorMessage || 'An error occurred while submitting your exam. Please try again.'}
              </Typography>
            </Box>
          </Fade>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only allow closing if it's not a backdrop click or escape key
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }} 
      className={classes.modal} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogContent className={classes.content}>
        <Typography variant="h5" className={classes.title}>
          Submitting Your Exam
        </Typography>
        
        <div className={classes.progressContainer}>
          <LinearProgress 
            variant="determinate" 
            value={animatedProgress} 
            className={classes.progressBar}
          />
          <Typography variant="body2" style={{ marginTop: 8, opacity: 0.8 }}>
            {Math.round(animatedProgress)}% Complete
          </Typography>
        </div>

        <Box>
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`${classes.stepContainer} ${getStepClass(index)}`}
            >
              {getStepIcon(index)}
              <Typography className={classes.stepText}>
                {step}
              </Typography>
            </div>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionProgressModal; 